import {
  AloftArea,
  CraftsmanCostProps,
  DEFAULT_CORNERS,
  ORDER_FEATURES,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';

import { selectEffectiveDate } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createFeatureCostSelectors } from '../utils/createFeatureCostSelectors.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateADUCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const propertyIdentificationForTransaction = propertyIdentification[id];

  if (
    isUndefined(improvementsForTransaction) ||
    isUndefined(propertyIdentificationForTransaction)
  ) {
    return undefined;
  }

  // for now we are just using corners from the main dwelling, which is
  // currently just always 6. also, bathrooms are included in the sfr
  // cost, and we are currently only collecting the inputs for reference.
  const { adu, corners } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = propertyIdentificationForTransaction;

  if (
    isUndefined(zipCode) ||
    isUndefined(adu) ||
    isUndefined(adu.area) ||
    isUndefined(adu.quality) ||
    isUndefined(effectiveDate) ||
    !ORDER_FEATURES.adu.hasFeature(improvementsForTransaction)
  ) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getADUCost({
    quality: adu.quality,
    sqft: AloftArea.schema.parse(adu.area),
    fullBaths: adu.fullBaths,
    halfBaths: adu.halfBaths,
    zipCode,
    corners: corners ?? DEFAULT_CORNERS,
  });
};

export const { selectADUCost, selectZipOnlyADUCost, selectADUCosts, selectADUCostDisplay } =
  createFeatureCostSelectors({
    calculate: calculateADUCost,
    inputSelectors: [
      selectCostProps,
      selectImprovementsEntities,
      selectPropertyIdentificationEntities,
      selectEffectiveDate,
    ],
    names: {
      cost: 'selectADUCost',
      zipOnly: 'selectZipOnlyADUCost',
      costs: 'selectADUCosts',
      display: 'selectADUCostDisplay',
    },
  });
